import React, { useState, useEffect, Fragment, useRef } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Badge, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint, faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import SalesImg from "../../Assets/Images/Dashboard/sales.png";
import ExpensesImg from "../../Assets/Images/Dashboard/expenses.png";
import ReceivablesImg from "../../Assets/Images/Dashboard/receivables.png";
import NetsalesImg from "../../Assets/Images/Dashboard/netsales.png";
import MytLogo from "../../Assets/Images/Login/logo.png";

import dayjs from "dayjs";
import Navbar from "../../Components/Navbar/Navbar";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import "../../Components/Navbar/Navbar.css";
import "./Dashboard.css";
import {
  formatDateNoTime,
  getName,
  getTime,
  getType,
  numberFormat,
  TokenExpiry,
  getDashboardPrintStyles,
} from "../../Helpers/Utils/Common";
import { getDashboardReport } from "../../Helpers/apiCalls/DashboardReport/DashboardReportApi";
import { searchReceivablesAgingReport, getFinancialReport } from "../../Helpers/apiCalls/SalesReport/SalesReportApi";
import { searchProjectExpense } from "../../Helpers/apiCalls/ProjectInvoice/ProjectExpenseApi";



export default function Dashboard3() {
  let navigate = useNavigate();
  const type = getType();
  const chartContainerRef = useRef();
  const [inactive, setInactive] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [data, setData] = useState({
    sales: 0,
    expenses: 0,
    net_sales: 0,
    receivables: 0,
    pending_invoice: 0,
    pending_expense: 0,
    projects_to_bill: 0,
    for_approval_po: 0,
    unpaid_po: 0,
    for_approval_project_expense: 0,
    petty_cash: 0,
    open_invoice: 0,
  });

  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [expenseFilter, setExpenseFilter] = useState("all"); // "all", "operating", "project";
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const [pieChartData, setPieChartData] = useState({
    labels: [],
    datasets: []
  });
  const days = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
  };

  // Dashboard Data
  async function fetchData() {
    const response = await getDashboardReport();

    if (response.data) {
      const dashboardData = response.data.data;

      // Get accurate receivables total from aging report
      const receivablesResponse = await searchReceivablesAgingReport({
        customer_id: "",
        project_id: "",
        date_from: "",
        date_to: "",
        payable: "",
        paid: "",
      });

      if (receivablesResponse.data) {
        // Use the total from receivables aging report instead of dashboard API
        dashboardData.receivables = receivablesResponse.data.summary?.total_receivables || 0;
      }

      // Get accurate expenses total and net sales from financial report
      const currentMonth = dayjs().month() + 1; // dayjs months are 0-based, so add 1
      const currentYear = dayjs().year();

      const financialResponse = await getFinancialReport({
        date_from: dayjs().startOf("month").format("YYYY-MM-DD"),
        date_to: dayjs().endOf("month").format("YYYY-MM-DD"),
        year: currentYear,
      });

      if (financialResponse.data) {
        const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                          'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        const currentMonthKey = monthKeys[currentMonth - 1];

        let totalExpenses = 0;
        let totalSales = 0;
        let netSales = 0;

        financialResponse.data.account_types.forEach((accountType) => {
          const accountName = accountType.name.toLowerCase();
          const monthValue = parseFloat(accountType[currentMonthKey]) || 0;

          // Calculate expenses (Operating + Project Expenses)
          if (accountName.includes('expense')) {
            totalExpenses += monthValue;
          }

          // Calculate sales
          if (accountName === 'sales') {
            totalSales += monthValue;
          }

          // Get the INCOME/LOSS value directly (this is the net sales we want)
          if (accountName === 'income/loss') {
            netSales = monthValue;
          }
        });

        // Update dashboard data
        dashboardData.expenses = totalExpenses;
        dashboardData.sales = totalSales;
        dashboardData.net_sales = netSales; // This is the INCOME/LOSS from the report
      }

      // Get accurate count of unpaid project expenses
      const unpaidProjectExpensesResponse = await searchProjectExpense({
        status: "approved", // "approved" status means unpaid in project expenses
      });

      if (unpaidProjectExpensesResponse.data) {
        // Count the unpaid project expenses
        dashboardData.petty_cash = unpaidProjectExpensesResponse.data.data?.length || 0;
      }

      setData(dashboardData);
    } else if (response.error) {
      TokenExpiry(response);
    }
  }

  // Fetch annual chart data
  async function fetchAnnualChartData() {
    const response = await getFinancialReport({
      date_from: dayjs().year(selectedYear).startOf("year").format("YYYY-MM-DD"),
      date_to: dayjs().year(selectedYear).endOf("year").format("YYYY-MM-DD"),
      year: selectedYear,
    });

    if (response.data) {
      const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                        'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const monthLabels = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];

      let salesData = new Array(12).fill(0);
      let expensesData = new Array(12).fill(0);

      response.data.account_types.forEach((accountType) => {
        const accountName = accountType.name.toLowerCase();

        monthKeys.forEach((monthKey, index) => {
          const monthValue = parseFloat(accountType[monthKey]) || 0;

          // Calculate sales
          if (accountName === 'sales') {
            salesData[index] += monthValue;
          }

          // Calculate expenses (Operating + Project Expenses)
          if (accountName.includes('expense')) {
            expensesData[index] += monthValue;
          }
        });
      });

      setChartData({
        labels: monthLabels,
        datasets: [
          {
            label: 'Sales',
            data: salesData,
            backgroundColor: 'rgba(54, 162, 235, 0.8)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
          },
          {
            label: 'Expenses',
            data: expensesData,
            backgroundColor: 'rgba(75, 192, 192, 0.8)',
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 1,
          }
        ]
      });
    }
  }

  // Fetch annual pie chart data for account types
  async function fetchAnnualPieChartData() {
    const response = await getFinancialReport({
      date_from: dayjs().year(selectedYear).startOf("year").format("YYYY-MM-DD"),
      date_to: dayjs().year(selectedYear).endOf("year").format("YYYY-MM-DD"),
      year: selectedYear,
    });

    if (response.data) {
      const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                        'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

      // Collect all account types from expense categories
      const accountTypeData = {};

      response.data.account_types.forEach((accountType) => {
        const accountName = accountType.name.toLowerCase();

        // Filter based on expense filter selection
        let shouldInclude = false;
        if (expenseFilter === "all" && accountName.includes('expense')) {
          shouldInclude = true;
        } else if (expenseFilter === "operating" && accountName.includes('operating') && accountName.includes('expense')) {
          shouldInclude = true;
        } else if (expenseFilter === "project" && accountName.includes('project') && accountName.includes('expense')) {
          shouldInclude = true;
        }

        // Only process expense account types based on filter
        if (shouldInclude) {
          // Check if this account type has children (sub-account types)
          if (accountType.children && accountType.children.length > 0) {
            accountType.children.forEach((childAccount) => {
              const childName = childAccount.name;
          if (childName.includes('Darmae Personal')) {
            return; 
             }
    
              let totalAmount = 0;

              // Sum up all months for this account type
              monthKeys.forEach((monthKey) => {
                totalAmount += parseFloat(childAccount[monthKey]) || 0;
              });

              if (totalAmount > 0) {
                if (accountTypeData[childName]) {
                  accountTypeData[childName] += totalAmount;
                } else {
                  accountTypeData[childName] = totalAmount;
                }
              }
            });
          } else {
            // If no children, use the parent account type
            let totalAmount = 0;
            monthKeys.forEach((monthKey) => {
              totalAmount += parseFloat(accountType[monthKey]) || 0;
            });

            if (totalAmount > 0) {
              if (accountTypeData[accountType.name]) {
                accountTypeData[accountType.name] += totalAmount;
              } else {
                accountTypeData[accountType.name] = totalAmount;
              }
            }
          }
        }
      });

      // Convert to arrays for chart
      const labels = Object.keys(accountTypeData);
      const data = Object.values(accountTypeData);

      // Generate colors for pie chart
      const colors = [
        'rgba(255, 99, 132, 0.8)',
        'rgba(54, 162, 235, 0.8)',
        'rgba(255, 205, 86, 0.8)',
        'rgba(75, 192, 192, 0.8)',
        'rgba(153, 102, 255, 0.8)',
        'rgba(255, 159, 64, 0.8)',
        'rgba(199, 199, 199, 0.8)',
        'rgba(83, 102, 255, 0.8)',
        'rgba(255, 99, 255, 0.8)',
        'rgba(99, 255, 132, 0.8)',
      ];

      const borderColors = [
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)',
        'rgba(255, 205, 86, 1)',
        'rgba(75, 192, 192, 1)',
        'rgba(153, 102, 255, 1)',
        'rgba(255, 159, 64, 1)',
        'rgba(199, 199, 199, 1)',
        'rgba(83, 102, 255, 1)',
        'rgba(255, 99, 255, 1)',
        'rgba(99, 255, 132, 1)',
      ];

      // Update chart title based on filter
      let chartTitle = 'Annual Expenses by Account Type';
      if (expenseFilter === "operating") {
        chartTitle = 'Annual Operating Expenses by Account Type';
      } else if (expenseFilter === "project") {
        chartTitle = 'Annual Project Expenses by Account Type';
      }

      setPieChartData({
        labels: labels,
        datasets: [
          {
            label: chartTitle,
            data: data,
            backgroundColor: colors.slice(0, labels.length),
            borderColor: borderColors.slice(0, labels.length),
            borderWidth: 1,
          }
        ]
      });
    }
  }

  // Register Chart.js components
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
  );

  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#000',
          font: {
            size: isMobile ? 10 : 12
          },
          padding: isMobile ? 10 : 20
        }
      },
      title: {
        display: true,
        text: `Sales vs. Expenses Comparison - ${selectedYear}`,
        font: {
          size: isMobile ? 12 : 16,
          weight: 'bold'
        },
        color: '#000',
        padding: {
          top: isMobile ? 10 : 20,
          bottom: isMobile ? 15 : 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        titleColor: '#000',
        bodyColor: '#000',
        titleFont: {
          size: isMobile ? 11 : 13
        },
        bodyFont: {
          size: isMobile ? 10 : 12
        },
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: PHP ${numberFormat(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#000',
          font: {
            size: isMobile ? 9 : 11
          },
          maxRotation: isMobile ? 45 : 0,
          minRotation: isMobile ? 45 : 0
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#000',
          font: {
            size: isMobile ? 9 : 11
          },
          callback: function(value) {
            return isMobile ? 'PHP ' + numberFormat(value, 0) : 'PHP ' + numberFormat(value);
          }
        }
      }
    },
  };

  // Generate pie chart title based on filter
  const getPieChartTitle = () => {
    let baseTitle = 'Annual Expenses by Account Type';
    if (expenseFilter === "operating") {
      baseTitle = 'Annual Operating Expenses by Account Type';
    } else if (expenseFilter === "project") {
      baseTitle = 'Annual Project Expenses by Account Type';
    }
    return `${baseTitle} - ${selectedYear}`;
  };

  // Pie chart options
  const pieChartOptions = {
    responsive: true,
    layout: {
      padding: {
        top: isMobile ? 5 : 10,
        bottom: isMobile ? 10 : 20,
        left: isMobile ? 10 : 20,
        right: isMobile ? 10 : 20
      }
    },
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right',
        labels: {
          color: '#000',
          font: {
            size: isMobile ? 9 : 12
          },
          padding: isMobile ? 8 : 20,
          usePointStyle: isMobile,
          pointStyle: isMobile ? 'circle' : 'rect',
          boxWidth: isMobile ? 8 : 12,
          boxHeight: isMobile ? 8 : 12
        }
      },
      title: {
        display: true,
        text: getPieChartTitle(),
        font: {
          size: isMobile ? 11 : 16,
          weight: 'bold'
        },
        color: '#000',
        padding: {
          top: isMobile ? 5 : 10,
          bottom: isMobile ? 10 : 25
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        titleColor: '#000',
        bodyColor: '#000',
        titleFont: {
          size: isMobile ? 10 : 13
        },
        bodyFont: {
          size: isMobile ? 9 : 12
        },
        callbacks: {
          label: function(context) {
            const data = context.dataset.data;
            const total = data.reduce((acc, value) => acc + value, 0);
            const currentValue = data[context.dataIndex];
            const percentage = ((currentValue / total) * 100).toFixed(2);
            const label = context.label || "";
            return isMobile
              ? `${label}: ${percentage}%`
              : `${label}: PHP ${numberFormat(currentValue)} (${percentage}%)`;
          }
        }
      }
    },
  };

  // Generate year options for the filter
  const generateYearOptions = () => {
    const currentYear = dayjs().year();
    const years = [];
    for (let i = currentYear; i >= currentYear - 10; i--) {
      years.push({
        value: i,
        label: i.toString()
      });
    }
    return years;
  };

  // Generate expense filter options
  const expenseFilterOptions = [
    { value: "all", label: "All Expenses" },
    { value: "operating", label: "Operating Expenses Only" },
    { value: "project", label: "Project Expenses Only" }
  ];

  // Print function for charts
  const handlePrintCharts = async () => {
    try {
      // Get all canvas elements (charts)
      const canvases = chartContainerRef.current.querySelectorAll('canvas');
      const chartImages = [];

      // Convert each canvas to image
      for (let i = 0; i < canvases.length; i++) {
        const canvas = canvases[i];
        const imageDataUrl = canvas.toDataURL('image/png', 1.0);
        chartImages.push(imageDataUrl);
      }

      // Get current date for the print header
      const currentDate = new Date().toLocaleDateString();
      const currentTime = new Date().toLocaleTimeString();

      // Create print window
      const printWindow = window.open('', '_blank');

      // Create print-friendly HTML with chart images
      const printHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Dashboard Charts - ${selectedYear}</title>
            <style>${getDashboardPrintStyles()}</style>
          </head>
          <body class="dashboard-print-body">
            <div class="dashboard-print-header">
              <img src="${MytLogo}" alt="MYT Logo" class="dashboard-print-logo" />
              <h1 class="dashboard-print-header-title">Annual Financial Report</h1>
              <p class="dashboard-print-header-subtitle">Generated on: ${currentDate} at ${currentTime}</p>
              <p class="dashboard-print-header-subtitle">Year: ${selectedYear} | Expense Filter: ${expenseFilterOptions.find(opt => opt.value === expenseFilter)?.label}</p>
            </div>

            <div class="dashboard-charts-container">
              ${chartImages.length > 0 ? `
                <div class="dashboard-chart-section">
                  <img src="${chartImages[0]}" alt="Bar Chart" class="dashboard-chart-image" />
                </div>

                ${chartImages.length > 1 ? `
                  <div class="dashboard-chart-section">
                    <img src="${chartImages[1]}" alt="Pie Chart" class="dashboard-chart-image" />
                  </div>
                ` : ''}
              ` : '<p class="dashboard-no-charts-message">No charts available to print.</p>'}
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(printHTML);
      printWindow.document.close();

      // Wait for images to load then print
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 1500);

    } catch (error) {
      alert('Error occurred while preparing charts for printing. Please try again.');
    }
  };

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
    fetchAnnualChartData();
    fetchAnnualPieChartData();
    // fetchOpenBranches();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchAnnualChartData();
      fetchAnnualPieChartData();
    }
  }, [selectedYear, expenseFilter]);

  return (
    <div>
      <div className="page">
        <Navbar
          onCollapse={(inactive) => {
            setInactive(inactive);
          }}
          active={"DASHBOARD"}
        />
      </div>
      <div className={`dashboard ${inactive ? "inactive" : "active"}`}>
        <div className={"dashboard-wrapper"}>
          <Row className="d-flex justify-content-between align-items-end">
            <Col xs={"auto"}>
              <h2 className="font-medium">
                Hello, <b>{getName()}!</b>👋
              </h2>
            </Col>
            <Col xs={"auto"}>
              <h3 className="date-and-time">{`${getTime(new Date())} ${
                days[new Date().getDay()]
              } | ${formatDateNoTime(new Date())}`}</h3>
            </Col>
          </Row>
          {type === "admin" ? (
            <Fragment>
              <Row className="d-flex">
                <Col className="mt-5">
                  <Row>
                    <Col className="box-1 d-flex justify-content-center align-items-center mx-2 cursor">
                      <div className="me-2 justify-content-center">
                        <img
                          src={SalesImg}
                          className="print-img"
                          style={{ width: "60px", height: "60px" }}
                          alt=""
                        />
                      </div>
                      <Row>
                        <div
                          className="big-hdr"
                          onClick={() => navigate("/incomestatementreport")}
                        >
                          Sales
                        </div>
                        <div
                          className="stats"
                          onClick={() => navigate("/incomestatementreport")}
                        >
                          PHP{numberFormat(data?.sales ?? '0')}
                        </div>
                        <div className="box-low-text">for the month</div>
                      </Row>
                    </Col>
                    <Col className="box-1 d-flex justify-content-center align-items-center mx-2 cursor">
                      <div className="me-2">
                        <img
                          src={ExpensesImg}
                          className="print-img"
                          style={{ width: "60px", height: "60px" }}
                          alt=""
                        />
                      </div>
                      <Row className="justify-content">
                        <div
                          className="big-hdr"
                          onClick={() => navigate("/incomestatementreport")}
                        >
                          Expenses
                        </div>
                        <div
                          className="stats"
                          onClick={() => navigate("/incomestatementreport")}
                        >
                          PHP{numberFormat(data.expenses ?? '0')}
                        </div>
                        <div className="box-low-text">for the month</div>
                      </Row>
                    </Col>
                    <Col className="box-1 d-flex justify-content-center align-items-center mx-2 cursor">
                      <div className="me-2">
                        <img
                          src={ReceivablesImg}
                          className="print-img"
                          style={{ width: "60px", height: "60px" }}
                          alt=""
                        />
                      </div>
                      <Row>
                        <div
                          className="big-hdr"
                          onClick={() => navigate("/receivablesagingreport")}
                        >
                          Receivables
                        </div>
                        <div
                          className="stats"
                          onClick={() => navigate("/receivablesagingreport")}
                        >
                          PHP{numberFormat(data.receivables ?? '0')}
                        </div>
                        <div className="box-low-text">for the month</div>
                      </Row>
                    </Col>
                    <Col className="box-1 d-flex justify-content-center align-items-center mx-2 cursor">
                      <div className="me-2">
                        <img
                          src={NetsalesImg}
                          className="print-img"
                          style={{ width: "60px", height: "60px" }}
                          alt=""
                        />
                      </div>
                      <Row>
                        <div
                          className="big-hdr"
                          onClick={() => navigate("/incomestatementreport")}
                        >
                          Net Sales
                        </div>
                        <div
                          className="stats"
                          onClick={() => navigate("/incomestatementreport")}
                        >
                          PHP
                          {numberFormat(data.net_sales ?? '0')}
                        </div>
                        <div className="box-low-text">for the month</div>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="d-flex">
                <Col className="mt-3">
                  <Row>
                    <Col className="box-1 mx-2">
                      <div className="me-2"></div>
                      <Row>
                        <div
                          className="header cursor"
                          onClick={() => navigate("/suppliesexpenses?tab=for_approval")}
                        >
                          PO for Approval
                        </div>
                        <div
                          className="box-text cursor"
                          onClick={() => navigate("/suppliesexpenses?tab=for_approval")}
                        >
                          {data.for_approval_po ?? '0'}
                        </div>
                        <div className="box-low-text"> Total Number</div>
                      </Row>
                    </Col>
                    <Col className="box-1 mx-2">
                      <div className="me-2"></div>
                      <Row>
                        <div
                          className="header cursor"
                          onClick={() => navigate("/suppliesexpenses?tab=incomplete")}
                        >
                          Unpaid PO
                        </div>
                        <div
                          className="box-text cursor"
                          onClick={() => navigate("/suppliesexpenses?tab=incomplete")}
                        >
                          {data.unpaid_po ?? '0'}
                        </div>
                        <div className="box-low-text"> Total Number</div>
                      </Row>
                    </Col>
                    <Col className="box-1 mx-2">
                      <div className="me-2"></div>
                      <Row>
                        <div
                          className="header cursor"
                          onClick={() => navigate("/projectexpense")}
                        >
                          For Approval Project Expense
                        </div>
                        <div
                          className="box-text cursor"
                          onClick={() => navigate("/projectexpense")}
                        >
                          {data.for_approval_project_expense ?? '0'}
                        </div>
                        <div className="box-low-text"> Total Number</div>
                      </Row>
                    </Col>
                    <Col className="box-1 mx-2">
                      <div className="me-2"></div>
                      <Row>
                        <div
                          className="header cursor"
                          onClick={() => navigate("/projectexpense?tab=approved")}
                        >
                          Unpaid Project Expense
                        </div>
                        <div
                          className="box-text cursor"
                          onClick={() => navigate("/projectexpense?tab=approved")}
                        >
                          {data.petty_cash ?? '0'}
                        </div>
                        <div className="box-low-text"> Total Number</div>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="d-flex">
                <Col className="mt-3">
                  <Row>
                    <Col className="box-1 mx-2">
                      <div className="me-2"></div>
                      <Row>
                        <div
                          className="header cursor red-text"
                          onClick={() => navigate("/forbilling")}
                        >
                          For Billing
                        </div>
                        <div
                          className="box-text cursor red-text"
                          onClick={() => navigate("/forbilling")}
                        >
                          {data.projects_to_bill ?? '0'}
                        </div>
                        <div className="box-low-text red-text"> Total Number</div>
                      </Row>
                    </Col>
                    <Col className="box-1 mx-2">
                      <div className="me-2"></div>
                      <Row>
                        <div
                          className="header cursor"
                          onClick={() => navigate("/projectinvoice?tab=pending")}
                        >
                          Pending Invoice
                        </div>
                        <div
                          className="box-text cursor"
                          onClick={() => navigate("/projectinvoice?tab=pending")}
                        >
                          {data.pending_invoice ?? '0'}
                        </div>
                        <div className="box-low-text"> Total Number</div>
                      </Row>
                    </Col>
                    <Col className="box-1 mx-2">
                      <div className="me-2"></div>
                      <Row>
                        <div
                          className="header cursor"
                          onClick={() => navigate("/projectinvoice?tab=open_bill")}
                        >
                          Open Invoice
                        </div>
                        <div
                          className="box-text cursor"
                          onClick={() => navigate("/projectinvoice?tab=open_bill")}
                        >
                          {data.open_invoice ?? '0'}
                        </div>
                        <div className="box-low-text"> Total Number</div>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>

              {/* Combined Charts Container */}
              <Row className="d-flex mt-4">
                <Col>
                  <div
                    ref={chartContainerRef}
                    className="chart-container"
                  >

                    {/* Print Button and Year Filter Row */}
                    <Row className="mb-3">
                      <Col xs={12} className="d-flex justify-content-end align-items-center">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>Year:</span>
                            <Select
                              value={selectedYear}
                              onChange={setSelectedYear}
                              options={generateYearOptions()}
                              style={{ width: 100 }}
                              size="small"
                            />
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handlePrintCharts}
                            className="print-button"
                          >
                            <FontAwesomeIcon icon={faPrint} />
                            Print Charts
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col>
                        <div style={{ height: '400px', width: '100%' }}>
                          <Bar
                            data={chartData}
                            options={{
                              ...chartOptions,
                              maintainAspectRatio: false,
                            }}
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Pie Chart Section */}
                    <Row className="mb-3">
                      <Col xs={8}>
                        {/* Title removed - redundant with chart title */}
                      </Col>
                      <Col xs={4} className="d-flex justify-content-end align-items-center">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500' }}>Filter:</span>
                          <Select
                            value={expenseFilter}
                            onChange={setExpenseFilter}
                            options={expenseFilterOptions}
                            style={{ width: 180 }}
                            size="small"
                            placeholder="Select expense type"
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col>
                        <div style={{ height: isMobile ? '700px' : '650px', width: '100%'}}>
                          <Pie
                            data={pieChartData}
                            options={{
                              ...pieChartOptions,
                              maintainAspectRatio: false,
                            }}
                          />
                        </div>
                      </Col>
                    </Row>

                  </div>
                </Col>
              </Row>
            </Fragment>
          ) : (
            /* Content for non-admin users */
            <div>Content for non-admin users</div>
          )}
        </div>
      </div>
    </div>
  );
}
